import { getSharedStyleSheet } from './sharedStyleSheet.js';

class TextInputButton extends HTMLElement {
    static observedAttributes = ['input', 'button', 'buttontitle', 'onbuttonclick', 'value', 'autoselect', 'disabled'];

    constructor() {
        super();
        this._onbuttonclick = null;
    }


    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open'});

            this.shadowRoot.host.style.display = 'inline-block';

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.style.display = 'flex';
            this._wrapper.style.width = '100%';
            this._wrapper.style.height = '100%';

            this._input = document.createElement('input');
            this._input.style.margin = '0';
            this._input.style.flexGrow = '1';
            this._input.style.border = 'var(--input-border-line-style) var(--input-border-color)';
            this._input.style.borderRadius = 'var(--input-border-radius) 0 0 var(--input-border-radius)';
            this._input.style.outline = 'none';
            this._input.style.padding = 'var(--input-padding)';
            this._input.style.margin = 'var(--input-margin) 0 var(--input-margin)';
            this._input.style.width = '100%';
            this._input.style.minWidth = '30px';

            this._input.style.borderColor = 'var(--input-border-color)';
            this._input.style.backgroundColor = 'var(--input-bg-color)';
            this._input.style.color = 'var(--input-text-color)';

            this._wrapper.appendChild(this._input);

            this._button = document.createElement('button');
            this._button.style.borderRadius = '0 var(--input-border-radius) var(--input-border-radius) 0';
            this._button.style.borderLeft = 'none';
            this._button.style.padding = '0 .5rem';
            this._button.style.margin = 'var(--input-margin) 0 var(--input-margin)';

            this._button.style.borderColor = 'var(--input-border-color)';
            this._button.style.backgroundColor = 'var(--page-bg-color)';
            this._button.style.color = 'var(--page-text-color)';

            const tooltip = this.getAttribute("title");

            if (tooltip) {
                this._button.title = tooltip;
                this._button.ariaLabel = tooltip;
            }

            this._button.addEventListener('mouseenter', (e) => {
                if (!this.getAttribute('disabled')) {
                    this._button.style.borderColor = 'var(--input-border-color-hover) var(--input-border-color-hover) var(--input-border-color-hover) transparent';
                    this._input.style.borderRightColor = 'var(--input-border-color-hover)';
                }
            });

            this._button.addEventListener('mouseleave', (e) => {
                if (!this.getAttribute('disabled')) {
                    this._button.style.borderColor = 'var(--input-border-color) var(--input-border-color) var(--input-border-color) transparent';
                    this._input.style.borderRightColor = 'var(--input-border-color)';
                }
            });

            if (!!this.autoselect) {
                this._input.addEventListener('focus', (e) => {
                    this._input.select();
                });
            }

            this._wrapper.appendChild(this._button);
            this.shadowRoot.appendChild(this._wrapper);

            this._button.addEventListener('click', (e) => {
                const handler = this.onbuttonclick;
                if (handler) handler(e);
            });

            this._input.addEventListener('keyup', (e) => {
                this.setAttribute('value', this._input.value);
            });

            this._input.addEventListener('change', (e) => {
                this.shadowRoot.dispatchEvent(new Event('change', {
                    composed: true,
                    bubbles: true
                }));
            });
        }

        this.dynamicRender();
    }

    dynamicRender() {
        if (!this._input || !this._button) return;

        this._input.value = this.value;
        this._button.textContent = this.buttontitle;

        this._input.readOnly =  !!this.getAttribute('readonly');

        if (!!this.getAttribute('disabled')) {
            this._input.disabled = true;
            this._button.disabled = true;

            this._input.style.borderColor = 'var(--input-border-color-disabled)';
            this._input.style.color = 'var(--input-text-color-disabled)';

            this._button.style.borderColor = 'var(--input-border-color-disabled) var(--input-border-color-disabled) var(--input-border-color-disabled) transparent';
            this._button.style.color = 'var(--input-text-color-disabled)';

        } else {
            this._input.disabled = false;
            this._button.disabled = false;

            this._input.style.borderColor = 'var(--input-border-color)';
            this._input.style.backgroundColor = 'var(--input-bg-color)';
            this._input.style.color = 'var(--input-text-color)';
            this._input.style.borderRightColor = 'var(--input-border-color)';

            this._button.style.borderColor = 'var(--input-border-color) var(--input-border-color) var(--input-border-color) transparent';
            this._button.style.backgroundColor = 'var(--page-bg-color)';
            this._button.style.color = 'var(--page-text-color)';
        }

        this._input.readOnly = !!this.getAttribute('readonly');
    }

    get textarea() {
        return this._input;
    }

    get button() {
        return this._button;
    }

    get buttontitle() {
        return this.getAttribute('buttontitle');
    }

    set buttontitle(_title) {
        this.setAttribute("buttontitle", _title);
        this.dynamicRender();
    }

    get value() {
        return this.getAttribute('value');
    }

    set value(_val) {
        this.setAttribute("value", _val);
        this.dynamicRender();
    }

    get autoselect() {
        return this.getAttribute('autoselect');
    }

    set autoselect(_autoselect) {
        this.setAttribute("autoselect", _autoselect);
    }

    get onbuttonclick() {
        return this._onbuttonclick;
    }

    set onbuttonclick(_handler) {
        this._onbuttonclick = _handler;
    }
}

class TextInputCopyButton extends TextInputButton {
    constructor() {
        super();

        this._onbuttonclick = (e) => {
            this._input.select();
            document.execCommand("copy");

            this._button.style.width = '3em';

            // Show tick icon
            this._button.innerHTML = '&#10003;'

            window.setTimeout(() => {
                this.button.textContent = this.buttontitle;
                this._button.style.width = '3em';
            }, 2000);
       };
    }

    connectedCallback() {
        super.connectedCallback();

        this._button.style.width = '3em';
        this.buttontitle = "⧉";

        const tooltip = this.getAttribute("title");

        if (!tooltip) {
            this.setAttribute("title", "Copy");
        }
    }
}

customElements.define('text-input-button', TextInputButton);
customElements.define('text-input-copy-button', TextInputCopyButton);
